// src/api/v2/contactImports.ts
import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { LaravelCollection, LaravelPaginated, LaravelResource } from '../types'
import type {
  BulkPayload, ContactSearchHit, ImportItem, ImportItemDetail, ImportRun, ImportSource, ImportSourcePayload,
  ImportSourceType, ItemUpdatePayload, ItemsQuery, SourceCategory, SourceTestResult,
} from '@/models/ContactImport'

const SOURCES = '/api/v2/contact-import-sources'
const RUNS = '/api/v2/contact-import-runs'

// ---- Sources ----
export function getImportSources(): Promise<AxiosResponse<LaravelCollection<ImportSource>>> {
  return request({ url: SOURCES, method: 'get' })
}
export function createImportSource(data: ImportSourcePayload): Promise<AxiosResponse<LaravelResource<ImportSource>>> {
  return request({ url: SOURCES, method: 'post', data })
}
export function updateImportSource(id: string, data: ImportSourcePayload): Promise<AxiosResponse<LaravelResource<ImportSource>>> {
  return request({ url: `${SOURCES}/${id}`, method: 'patch', data })
}
export function deleteImportSource(id: string): Promise<AxiosResponse<void>> {
  return request({ url: `${SOURCES}/${id}`, method: 'delete' })
}
export function testImportSource(
  data: { id: string } | { type: ImportSourceType; credentials: { token: string } },
): Promise<AxiosResponse<LaravelResource<SourceTestResult>>> {
  return request({ url: `${SOURCES}/test`, method: 'post', data })
}
export function getImportSourceCategories(id: string): Promise<AxiosResponse<LaravelCollection<SourceCategory>>> {
  return request({ url: `${SOURCES}/${id}/categories`, method: 'get' })
}

// ---- Runs ----
export function getImportRuns(page = 1): Promise<AxiosResponse<LaravelPaginated<ImportRun>>> {
  return request({ url: RUNS, method: 'get', params: { page } })
}
/** 204 when no run is open: the response body is then an empty string. */
export function getCurrentImportRun(): Promise<AxiosResponse<LaravelResource<ImportRun> | ''>> {
  return request({ url: `${RUNS}/current`, method: 'get' })
}
/** A SevDesk source takes its categories from the integration's type mapping (#274), so no mapping is sent. */
export function createImportRun(data: { source_id: string; category_mapping?: Record<string, string | null> }): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: RUNS, method: 'post', data })
}
export function getImportRun(id: string): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: `${RUNS}/${id}`, method: 'get' })
}
export function applyImportRun(id: string, suppress_webhooks: boolean): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: `${RUNS}/${id}/apply`, method: 'post', data: { suppress_webhooks } })
}
export function cancelImportRun(id: string): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: `${RUNS}/${id}/cancel`, method: 'post' })
}
export function remapImportCategory(id: string, category: string, type_id: string | null): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: `${RUNS}/${id}/remap-category`, method: 'post', data: { category, type_id } })
}
export function searchImportContacts(id: string, q: string): Promise<AxiosResponse<LaravelCollection<ContactSearchHit>>> {
  return request({ url: `${RUNS}/${id}/contacts/search`, method: 'get', params: { q } })
}

// ---- Items ----
export function getImportItems(runId: string, query: ItemsQuery): Promise<AxiosResponse<LaravelPaginated<ImportItem>>> {
  return request({ url: `${RUNS}/${runId}/items`, method: 'get', params: query })
}
export function getImportItem(runId: string, itemId: string): Promise<AxiosResponse<LaravelResource<ImportItemDetail>>> {
  return request({ url: `${RUNS}/${runId}/items/${itemId}`, method: 'get' })
}
export function updateImportItem(runId: string, itemId: string, data: ItemUpdatePayload): Promise<AxiosResponse<LaravelResource<ImportItemDetail>>> {
  return request({ url: `${RUNS}/${runId}/items/${itemId}`, method: 'patch', data })
}
export function bulkImportItems(runId: string, data: BulkPayload): Promise<AxiosResponse<LaravelResource<{ updated: number }>>> {
  return request({ url: `${RUNS}/${runId}/items/bulk`, method: 'post', data })
}

// ---- Admin mirror ----
export function getAdminImportSources(accountId: string): Promise<AxiosResponse<LaravelCollection<ImportSource>>> {
  return request({ url: `/api/v2/admin/accounts/${accountId}/contact-import-sources`, method: 'get' })
}
export function getAdminImportRuns(accountId: string, page = 1): Promise<AxiosResponse<LaravelPaginated<ImportRun>>> {
  return request({ url: `/api/v2/admin/accounts/${accountId}/contact-import-runs`, method: 'get', params: { page } })
}
export function getAdminImportRun(accountId: string, runId: string): Promise<AxiosResponse<LaravelResource<ImportRun>>> {
  return request({ url: `/api/v2/admin/accounts/${accountId}/contact-import-runs/${runId}`, method: 'get' })
}
